import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { base as api, apiBaseUrl } from "./base";
import { logout as apiLogout } from "./logout";

/* ───────── instancia sin interceptores para /auth/refresh ───────── */
const refreshClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true, // envía/recibe la cookie HttpOnly 'refresh'
});

/* Flags & cola */
let isRefreshing = false;
let queue: ((t: string) => void)[] = [];

/* Access token SOLO en memoria */
let accessToken: string | null = null;
export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}

/* Flag no sensible que indica "debería haber refresh cookie" */
function setHasRefreshFlag(on: boolean) {
  try {
    if (on) localStorage.setItem("hasRefresh", "1");
    else localStorage.removeItem("hasRefresh");
  } catch {}
}
function hasRefreshFlag(): boolean {
  try {
    return localStorage.getItem("hasRefresh") === "1";
  } catch {
    return false;
  }
}

/* ───────── utilidades ───────── */
const msgFromResponse = (data: any, fallback: string) => {
  if (!data) return fallback;
  if (Array.isArray((data as any).message)) return (data as any).message.join("\n");
  if (typeof (data as any).message === "string") return (data as any).message;
  return fallback;
};

const isPendingLimitError = (data: any) => {
  const directCode = data?.code ?? data?.errorCode;
  const nestedMsg = data?.message && typeof data.message === "object" ? data.message : undefined;
  const nestedCode = nestedMsg?.code ?? nestedMsg?.errorCode;
  return directCode === "PENDING_LIMIT" || nestedCode === "PENDING_LIMIT";
};

/** Marca un error como gestionado (ya se mostró toast) y lo rechaza sin romper la app */
function handledReject(error: AxiosError, fallback: string) {
  const msg = msgFromResponse((error as any).response?.data, fallback);
  if (msg) toast.error(msg);
  (error as any).__handled = true; // bandera para detectar en la UI
  return Promise.reject(error);
}

function setAuthHeader(config: AxiosRequestConfig, token: string) {
  if (!config.headers) (config as any).headers = {};
  const h: any = config.headers;
  if (typeof h.set === "function") {
    h.set("Authorization", `Bearer ${token}`);
  } else {
    h["Authorization"] = `Bearer ${token}`;
  }
}

/** Lee una cookie del documento (escapa nombre correctamente) */
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const pattern = "(?:^|; )" + name.replace(/[$()*+./?[\\\]^{|}-]/g, "\\$&") + "=([^;]*)";
  const m = document.cookie.match(new RegExp(pattern));
  return m ? decodeURIComponent(m[1]) : null;
}

/* 🔁 helper de redirección seguro (sin depender del Router) */
function redirect(path: string) {
  if (typeof window === "undefined") return;
  if (window.location.pathname === path) return; // evita bucles
  window.location.replace(path); // replace para no dejar la anterior en el historial
}

/* ───────── Interceptor en refreshClient: añade X-CSRF-Token ───────── */
refreshClient.interceptors.request.use((cfg) => {
  const csrf = getCookie("csrf");
  if (csrf) {
    const h: any = cfg.headers ?? {};
    if (typeof h.set === "function") h.set("X-CSRF-Token", csrf);
    else h["X-CSRF-Token"] = csrf;
    (cfg as any).headers = h;
  }
  return cfg;
});

/* ───────── helper: refresh token (usa cookie HttpOnly + CSRF header) ───────── */
const refreshAccessToken = async (): Promise<string> => {
  const { data } = await refreshClient.post("/auth/refresh");
  const token = data.access_token as string;
  setAccessToken(token);
  setHasRefreshFlag(true); // nos aseguramos de marcar que hay refresh activo
  return token;
};

/* ───────── REQUEST: añade Authorization (excepto /auth/login/refresh) ───────── */
api.interceptors.request.use((cfg) => {
  const url = (cfg.url ?? "").toString();
  if (!url.includes("/auth/login") && !url.includes("/auth/refresh")) {
    const token = getAccessToken();
    if (token) setAuthHeader(cfg, token);
  }
  return cfg;
});

/* ───────── RESPONSE: manejo 401/403/404/422/409/400/415/413/429 + fallback ───────── */
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const { status, data } = error.response || {};
    const original = error.config!;
    const url = (original?.url ?? "").toString();

    const isAuthLogin = url.includes("/auth/login");
    const isAuthRefresh = url.includes("/auth/refresh");

    // 401 en login → lo maneja la página
    if (status === 401 && isAuthLogin) return Promise.reject(error);

    // 401 genérico → intenta refresh (una vez)
    if (status === 401 && !isAuthRefresh && !(original as any)._retry) {
      // ⛔ No intentes refresh si NO hay señal de refresh + csrf
      if (!hasRefreshFlag() || !getCookie("csrf")) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push((token) => {
            setAuthHeader(original, token);
            api(original).then(resolve).catch(reject);
          });
        });
      }

      (original as any)._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        setAuthHeader(original, newToken);
        queue.forEach((cb) => cb(newToken));
        queue = [];
        return api(original);
      } catch (_err) {
        queue = [];
        setAccessToken(null);
        setHasRefreshFlag(false); // marca que ya no hay refresh
        await apiLogout();
        toast.error("Sesión expirada, por favor inicia sesión nuevamente.");
        // ⛔ evita crash fugaz: no rechazamos; dejamos la promesa pendiente hasta que navegue
        redirect("/login");
        return new Promise(() => {});
      } finally {
        isRefreshing = false;
      }
    }

    /* 403 → sin permiso */
    if (status === 403) {
      toast.error("No tienes permiso para realizar esta acción.");
      redirect("/403");
      // ⛔ no rechazar para evitar el flash de error en la UI
      return new Promise(() => {});
    }

    /* 404 → no encontrado */
    if (status === 404) {
      toast.error("Recurso no encontrado.");
      redirect("/404");
      // ⛔ no rechazar para evitar el flash de error en la UI
      return new Promise(() => {});
    }

    /* ✅ Validaciones/negocio: marcamos como gestionados para no romper la UI */
    if (status === 422) return handledReject(error, "Datos inválidos.");
    if (status === 409) {
      if (isPendingLimitError(data)) return Promise.reject(error);
      return handledReject(error, "Conflicto");
    }
    if (status === 400) return handledReject(error, "Petición inválida");
    if (status === 415) return handledReject(error, "Tipo de archivo no permitido.");
    if (status === 429)
      return handledReject(error, "Has alcanzado el límite de intentos. Prueba más tarde.");
    if (status === 413) return handledReject(error, "El archivo es demasiado grande.");

    if (!error.response) {
      return handledReject(error, "No se pudo conectar con el servidor.");
    }

    return handledReject(error, msgFromResponse(data, error.message || "Error inesperado"));
  }
);

/* Arranque opcional: intenta obtener un access token al cargar la app. */
export async function initAuthSilencioso(): Promise<string | null> {
  try {
    // Solo intenta si creemos que hay refresh cookie y vemos csrf
    if (!hasRefreshFlag() || !getCookie("csrf")) return null;
    const t = await refreshAccessToken();
    return t ?? null;
  } catch {
    setAccessToken(null);
    setHasRefreshFlag(false);
    return null;
  }
}

/* 👉 exporta helpers para que Login/Logout marquen/desmarquen el flag */
export const markHasRefresh = () => setHasRefreshFlag(true);
export const clearHasRefresh = () => setHasRefreshFlag(false);

/* (Opcional) helper para detectar en la UI si el error ya fue toasteado */
export const isHandledError = (e: any) => !!(e && (e as any).__handled);

export default api;
